/**
 * LandingPagePreviewRenderer - Live preview renderer for Page Builder Studio
 * 
 * Renders blocks in the canvas with click-to-select. Supports:
 * - Landing page blocks (hero, benefits, etc.) with element-level editing
 * - Data row blocks (series carousel, novel carousel, etc.) with live data
 * - Composable blocks (section, heading, paragraph, button, image)
 */
import React from 'react';
import BlockWrapper from './BlockWrapper';
import EditableHeroSection from './EditableHeroSection';
import EditableContentTypesSection from './EditableContentTypesSection';
import EditableBenefitsSection from './EditableBenefitsSection';
import EditableBuildPhaseSection from './EditableBuildPhaseSection';
import EditableCreatorsSection from './EditableCreatorsSection';
import EditableTransparencySection from './EditableTransparencySection';
import DataRowBlock from './DataRowBlock';
import { getBlockElements } from './ElementRegistry';
import { DATA_BLOCK_TYPES } from './AddSectionPanel';
import { ImageIcon } from 'lucide-react';

// Editable landing blocks (element-level editing)
const EDITABLE_LANDING_BLOCKS = {
  landing_hero: EditableHeroSection,
  landing_content_types: EditableContentTypesSection,
  landing_benefits: EditableBenefitsSection,
  landing_build_phase: EditableBuildPhaseSection,
  landing_creators: EditableCreatorsSection,
  landing_transparency: EditableTransparencySection,
};

// Block labels
const BLOCK_LABELS = {
  landing_hero: 'Hero Section',
  landing_content_types: 'Content Types',
  landing_benefits: 'Why Join',
  landing_build_phase: 'Build Phase Steps',
  landing_creators: 'Join The Movement',
  landing_transparency: 'Transparency Footer',
  section: 'Section',
  container: 'Container',
  heading: 'Heading',
  paragraph: 'Paragraph',
  button: 'Button',
  image: 'Image',
  gallery_grid: 'Gallery Grid',
};

// Data row type set for quick lookup
const DATA_ROW_TYPE_SET = new Set(Object.keys(DATA_BLOCK_TYPES));

// Composable structural/text/media types
const COMPOSABLE_TYPES = new Set([
  'section', 'container', 'heading', 'paragraph', 'button', 'image', 'gallery_grid',
]);

// Render a composable block (layout / text / media)
const ComposableBlockRenderer = ({ block }) => {
  const { type, props = {} } = block;

  if (type === 'section') {
    const bgStyle = {};
    if (props.backgroundColor && props.backgroundColor !== 'transparent') bgStyle.backgroundColor = props.backgroundColor;
    if (props.backgroundImage) {
      bgStyle.backgroundImage = `url(${props.backgroundImage})`;
      bgStyle.backgroundSize = 'cover';
      bgStyle.backgroundPosition = 'center';
    }
    return (
      <div
        className="px-4"
        style={{
          paddingTop: `${props.paddingTop ?? 48}px`,
          paddingBottom: `${props.paddingBottom ?? 48}px`,
          ...bgStyle,
        }}
        data-testid={`composable-section-${block.id}`}
      >
        <div style={{ maxWidth: props.maxWidth || '1280px', margin: '0 auto' }}>
          <div className="border-2 border-dashed border-zinc-700/40 rounded-lg p-8 min-h-[80px] flex items-center justify-center">
            <span className="text-zinc-600 text-sm">Empty Section</span>
          </div>
        </div>
      </div>
    );
  }

  if (type === 'container') {
    const containerStyle = {
      maxWidth: props.maxWidth || '1200px',
      padding: `${props.padding ?? 24}px`,
      backgroundColor: (props.backgroundColor && props.backgroundColor !== 'transparent') ? props.backgroundColor : undefined,
    };
    if (props.alignment === 'center') containerStyle.margin = '0 auto';
    else if (props.alignment === 'right') containerStyle.marginLeft = 'auto';
    return (
      <div style={containerStyle} data-testid={`composable-container-${block.id}`}>
        <div className="border-2 border-dashed border-zinc-800/40 rounded-lg p-6 min-h-[60px] flex items-center justify-center">
          <span className="text-zinc-600 text-sm">Container</span>
        </div>
      </div>
    );
  }

  if (type === 'heading') {
    const Tag = props.level || 'h2';
    const sizeMap = { h1: 'text-4xl sm:text-5xl', h2: 'text-2xl sm:text-3xl', h3: 'text-xl sm:text-2xl', h4: 'text-lg' };
    return (
      <div className="px-4 sm:px-6 py-3" data-testid={`composable-heading-${block.id}`}>
        <Tag
          className={`font-bold ${sizeMap[Tag] || 'text-2xl'}`}
          style={{
            textAlign: props.alignment || 'left',
            color: props.color || '#ffffff',
            maxWidth: props.maxWidth || undefined,
          }}
        >
          {props.text || 'Section Heading'}
        </Tag>
      </div>
    );
  }

  if (type === 'paragraph') {
    return (
      <div className="px-4 sm:px-6 py-2" data-testid={`composable-paragraph-${block.id}`}>
        <p
          className="text-base leading-relaxed whitespace-pre-wrap"
          style={{
            textAlign: props.alignment || 'left',
            color: props.color || '#a1a1aa',
            maxWidth: props.maxWidth || undefined,
          }}
        >
          {props.text || 'Add your content here...'}
        </p>
      </div>
    );
  }

  if (type === 'button') {
    const variantStyles = {
      primary: 'bg-orange-500 hover:bg-orange-600 text-white',
      secondary: 'bg-zinc-700 hover:bg-zinc-600 text-white',
      outline: 'border border-zinc-600 text-zinc-300 hover:bg-zinc-800',
      ghost: 'text-zinc-300 hover:text-white hover:bg-zinc-800/50',
    };
    const sizeStyles = {
      small: 'px-4 py-2 text-sm',
      medium: 'px-6 py-3 text-base',
      large: 'px-8 py-4 text-lg',
    };
    const alignMap = { left: 'justify-start', center: 'justify-center', right: 'justify-end' };

    return (
      <div
        className={`px-4 sm:px-6 py-3 flex ${alignMap[props.alignment] || 'justify-start'}`}
        data-testid={`composable-button-${block.id}`}
      >
        <button
          className={`rounded-lg font-medium transition-colors ${variantStyles[props.variant] || variantStyles.primary} ${sizeStyles[props.size] || sizeStyles.medium}`}
        >
          {props.text || 'Click Me'}
        </button>
      </div>
    );
  }

  if (type === 'image') {
    const radiusMap = { none: '0', sm: '4px', md: '8px', lg: '16px', xl: '24px', full: '9999px' };
    const radius = radiusMap[props.borderRadius] || radiusMap.md;

    return (
      <div className="px-4 sm:px-6 py-3" data-testid={`composable-image-${block.id}`}>
        {props.src ? (
          <img
            src={props.src.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${props.src}` : props.src}
            alt={props.alt || ''}
            className="w-full"
            style={{
              objectFit: props.objectFit || 'cover',
              maxHeight: props.maxHeight || '400px',
              borderRadius: radius,
            }}
          />
        ) : (
          <div
            className="aspect-video bg-zinc-800/50 flex flex-col items-center justify-center max-w-2xl mx-auto gap-2"
            style={{ borderRadius: radius }}
          >
            <ImageIcon className="w-8 h-8 text-zinc-600" />
            <span className="text-zinc-600 text-sm">Image Placeholder</span>
          </div>
        )}
      </div>
    );
  }

  if (type === 'gallery_grid') {
    const cols = props.columns || 3;
    return (
      <div className="px-4 sm:px-6 py-3" data-testid={`composable-gallery-${block.id}`}>
        <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
          {Array(cols * 2).fill(0).map((_, i) => (
            <div key={i} className="aspect-square bg-zinc-800/50 rounded-lg flex items-center justify-center">
              <ImageIcon className="w-6 h-6 text-zinc-600" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return null;
};

// Legacy section renderer (for section_* types from template registry)
const LegacySectionRenderer = ({ block }) => {
  const { type, props = {} } = block;
  const label = type.replace(/^section_/, '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  // Render based on section_* type
  if (type.includes('hero')) {
    return (
      <div className="relative py-16 px-6 bg-gradient-to-b from-zinc-900 to-black" data-testid={`legacy-${block.id}`}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-3">{props.title || label}</h2>
          <p className="text-zinc-400">{props.subtitle || 'Hero section'}</p>
        </div>
      </div>
    );
  }

  if (type.includes('trending') || type.includes('rail') || type.includes('releases')) {
    return (
      <div className="py-8 px-6" data-testid={`legacy-${block.id}`}>
        <h3 className="text-lg font-semibold text-white mb-4">{props.title || label}</h3>
        <div className="flex gap-4 overflow-hidden">
          {Array(5).fill(0).map((_, i) => (
            <div key={i} className="flex-shrink-0 w-40 h-56 bg-zinc-800/50 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (type.includes('grid') || type.includes('gallery')) {
    return (
      <div className="py-8 px-6" data-testid={`legacy-${block.id}`}>
        <h3 className="text-lg font-semibold text-white mb-4">{props.title || label}</h3>
        <div className="grid grid-cols-3 gap-4">
          {Array(6).fill(0).map((_, i) => (
            <div key={i} className="aspect-square bg-zinc-800/50 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (type.includes('creator') || type.includes('spotlight')) {
    return (
      <div className="py-8 px-6" data-testid={`legacy-${block.id}`}>
        <h3 className="text-lg font-semibold text-white mb-4">{props.title || label}</h3>
        <div className="flex gap-6 justify-center">
          {Array(3).fill(0).map((_, i) => (
            <div key={i} className="w-24 h-24 rounded-full bg-zinc-800/50" />
          ))}
        </div>
      </div>
    );
  }

  // Fallback for any section_* type
  return (
    <div className="py-8 px-6 border border-zinc-800/30 bg-zinc-900/30 mx-4 my-2 rounded-lg" data-testid={`legacy-${block.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-2 h-2 rounded-full bg-zinc-600" />
        <span className="text-xs text-zinc-500 font-mono">{type}</span>
      </div>
      <h3 className="text-base font-medium text-zinc-300">{props.title || label}</h3>
    </div>
  );
};

// ========== MAIN RENDERER ==========

const LandingPagePreviewRenderer = ({
  blocks = [],
  selectedBlockId,
  onSelectBlock,
  selectedElementId,
  onSelectElement,
  editingElementId,
  onStartEditing,
  onStopEditing,
  onUpdateElementValue,
  viewport = 'desktop',
  animationKey = 0,
  prefersReducedMotion = false,
  // Section operations
  onDuplicateBlock,
  onDeleteBlock,
  onMoveBlockUp,
  onMoveBlockDown,
}) => {
  const getViewportStyle = () => {
    switch (viewport) {
      case 'mobile': return { maxWidth: '375px', margin: '0 auto' };
      case 'tablet': return { maxWidth: '768px', margin: '0 auto' };
      default: return { width: '100%' };
    }
  };

  const handleBlockClick = (blockId) => {
    onSelectBlock(blockId);
    onSelectElement?.(null);
  };

  const handleBackgroundClick = () => {
    onSelectBlock(null);
    onSelectElement?.(null);
  };

  const enabledBlocks = blocks
    .filter(b => b.enabled !== false)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div
      className="min-h-full bg-black"
      style={getViewportStyle()}
      onClick={handleBackgroundClick}
      data-testid="landing-preview"
    >
      {enabledBlocks.length === 0 ? (
        <div className="h-full flex items-center justify-center py-20">
          <div className="text-center">
            <p className="text-zinc-500 mb-2">No blocks yet.</p>
            <p className="text-zinc-600 text-sm">Add blocks from the left panel to start building.</p>
          </div>
        </div>
      ) : (
        <div>
          {enabledBlocks.map((block) => {
            // 1. Landing page editable blocks
            const EditableComponent = EDITABLE_LANDING_BLOCKS[block.type];
            if (EditableComponent) {
              const label = BLOCK_LABELS[block.type] || block.type;
              return (
                <BlockWrapper
                  key={block.id}
                  blockId={block.id}
                  blockLabel={label}
                  settingsKey={block.settings_key}
                  isSelected={selectedBlockId === block.id && !selectedElementId}
                  onSelect={() => handleBlockClick(block.id)}
                  onDuplicate={onDuplicateBlock}
                  onDelete={onDeleteBlock}
                  onMoveUp={onMoveBlockUp}
                  onMoveDown={onMoveBlockDown}
                >
                  <EditableComponent
                    props={block.props || {}}
                    selectedElementId={selectedBlockId === block.id ? selectedElementId : null}
                    onSelectElement={(elementId) => {
                      onSelectBlock(block.id);
                      onSelectElement?.(elementId);
                    }}
                    editingElementId={editingElementId}
                    onStartEditing={onStartEditing}
                    onStopEditing={onStopEditing}
                    onUpdateElementValue={(elementId, newValue) => {
                      onUpdateElementValue?.(block.id, elementId, newValue);
                    }}
                    animationKey={animationKey}
                    disabled={prefersReducedMotion}
                  />
                </BlockWrapper>
              );
            }

            // 2. Data row blocks
            if (DATA_ROW_TYPE_SET.has(block.type)) {
              const def = DATA_BLOCK_TYPES[block.type];
              return (
                <BlockWrapper
                  key={block.id}
                  blockId={block.id}
                  blockLabel={def?.name || block.type}
                  settingsKey={block.settings_key}
                  isSelected={selectedBlockId === block.id}
                  onSelect={() => handleBlockClick(block.id)}
                  onDuplicate={onDuplicateBlock}
                  onDelete={onDeleteBlock}
                  onMoveUp={onMoveBlockUp}
                  onMoveDown={onMoveBlockDown}
                >
                  <DataRowBlock
                    blockId={block.id}
                    blockType={block.type}
                    props={block.props || {}}
                    isSelected={selectedBlockId === block.id}
                  />
                </BlockWrapper>
              );
            }

            // 3. Composable blocks (layout / text / media)
            if (COMPOSABLE_TYPES.has(block.type)) {
              const label = BLOCK_LABELS[block.type] || block.type;
              return (
                <BlockWrapper
                  key={block.id}
                  blockId={block.id}
                  blockLabel={label}
                  settingsKey={block.settings_key}
                  isSelected={selectedBlockId === block.id}
                  onSelect={() => handleBlockClick(block.id)}
                  onDuplicate={onDuplicateBlock}
                  onDelete={onDeleteBlock}
                  onMoveUp={onMoveBlockUp}
                  onMoveDown={onMoveBlockDown}
                >
                  <ComposableBlockRenderer block={block} />
                </BlockWrapper>
              );
            }

            // 4. Legacy section_* types from template registry (also handles unprefixed types)
            if (block.type.startsWith('section_') || 
                ['hero_minimal', 'hero_overlay', 'hero_split',
                 'trending_rail', 'featured_grid', 'new_releases_rail',
                 'continue_reading_section', 'genre_spotlight', 'community_picks',
                 'art_gallery_grid', 'chapter_list', 'creator_spotlight', 'creator_grid',
                 'creator_cta', 'leaderboard_strip', 'cta_banner', 'transparency_footer',
                 'announcement_banner', 'category_spotlight', 'admin_pages_list',
                ].includes(block.type)) {
              const label = block.type.replace(/^section_/, '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
              return (
                <BlockWrapper
                  key={block.id}
                  blockId={block.id}
                  blockLabel={label}
                  settingsKey={block.settings_key}
                  isSelected={selectedBlockId === block.id}
                  onSelect={() => handleBlockClick(block.id)}
                  onDuplicate={onDuplicateBlock}
                  onDelete={onDeleteBlock}
                  onMoveUp={onMoveBlockUp}
                  onMoveDown={onMoveBlockDown}
                >
                  <LegacySectionRenderer block={block} />
                </BlockWrapper>
              );
            }

            // 5. Unknown fallback
            return (
              <BlockWrapper
                key={block.id}
                blockId={block.id}
                blockLabel={block.type}
                settingsKey={block.settings_key}
                isSelected={selectedBlockId === block.id}
                onSelect={() => handleBlockClick(block.id)}
                onDuplicate={onDuplicateBlock}
                onDelete={onDeleteBlock}
                onMoveUp={onMoveBlockUp}
                onMoveDown={onMoveBlockDown}
              >
                <div className="p-8 bg-zinc-900/50 border border-zinc-700/30 text-center my-2 mx-4 rounded-lg">
                  <p className="text-zinc-500 text-sm">Unknown block: {block.type}</p>
                </div>
              </BlockWrapper>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LandingPagePreviewRenderer;
